// Referral V2 API functions (plain async, not hooks — the v2 module pattern,
// cf. loyalty.ts). All endpoints scope by the JWT business.
import client from "shared/utils/axios-config";
import {
  ConvertReferralResult,
  CustomerReferral,
  PendingReferralRow,
  ReferralConfigResponse,
  UpdateReferralConfigForm,
  ValidateReferralResult,
} from "shared/types/referral";

export const getReferralConfig = async (): Promise<ReferralConfigResponse> => {
  const res = await client.get(`/v2/referral/config`);
  return res.data.data;
};

export const updateReferralConfig = async (
  form: UpdateReferralConfigForm
): Promise<ReferralConfigResponse> => {
  const res = await client.put(`/v2/referral/config`, form);
  return res.data.data;
};

// Lifecycle (Manager-only): deactivate flips status ONLY — the config and
// every pending attribution are retained, and pendings still pay out (unlike
// loyalty's erase-on-deactivate). Saving a config is what activates.
export const deactivateReferralProgram = async (): Promise<void> => {
  const res = await client.post(`/v2/referral/program/deactivate`);
  return res.data.data;
};

export const getPendingReferrals = async (): Promise<{
  referrals: PendingReferralRow[];
}> => {
  const res = await client.get(`/v2/referral/pending`);
  return res.data.data;
};

// Live check behind the Isi Klien Baru referral field. Advisory — the server
// re-validates when the attribution is actually created on save.
export const validateReferralCode = async (args: {
  code: string;
  phoneNumber?: string;
}): Promise<ValidateReferralResult> => {
  const res = await client.get(`/v2/referral/validate`, {
    params: {
      code: args.code,
      ...(args.phoneNumber ? { phone_number: args.phoneNumber } : {}),
    },
  });
  return res.data.data;
};

export const getCustomerReferral = async (
  customerId: string
): Promise<CustomerReferral> => {
  const res = await client.get(`/v2/referral/customers/${customerId}`);
  return res.data.data;
};

// Records a purchase for a client with a pending referral, converting it in
// the same transaction when the purchase qualifies; apply_intro_discount
// additionally redeems the friend's discount against this very purchase.
export const convertReferralFirstPurchase = async (args: {
  packageId: number;
  customerId: string;
  applyIntroDiscount: boolean;
  paymentMethodId?: string | null;
}): Promise<ConvertReferralResult> => {
  const res = await client.post(`/v2/referral/convert/${args.packageId}`, {
    customer_id: args.customerId,
    apply_intro_discount: args.applyIntroDiscount,
    payment_method_id: args.paymentMethodId ?? null,
  });
  return res.data.data;
};
